import { Box, Typography } from '@mui/material'
import Cookies from 'js-cookie'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import { useDeleteCaseMutation } from '@/store/apiService'
import { removeSelectedRow } from '@/store/project'
import { setCurrentCaseId } from '@/store/sessionSlice'

interface DeleteProjectDialogProps {
  isOpen: boolean
  onClose: () => void
  caseId: string | number | null
  refetch?: () => void
  title?: string
  content?: React.ReactNode
  width?: string
}

const DeleteProject: React.FC<DeleteProjectDialogProps> = ({
  isOpen,
  onClose,
  caseId,
  refetch,
  title,
  content,
  width = '600px',
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'project.deleteProjectDialog',
  })

  const [deleteCase] = useDeleteCaseMutation()
  const [isLoading, setIsLoading] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const handleDelete = async () => {
    if (!caseId) return

    setIsLoading(true)
    try {
      const query = new URLSearchParams(location.search)
      const urlCaseId = query.get('caseId') || ''
      if (urlCaseId && caseId === urlCaseId) {
        dispatch(setCurrentCaseId(''))
        // If we are deleting the active case, navigate to projects
        navigate('/projects')
      }

      await deleteCase({ ids: [caseId as any], hard_delete: false }).unwrap()
      toast.success(t('deleteSuccess'))
      dispatch(removeSelectedRow(caseId as any))
      onClose()
      refetch?.()

      if (location.pathname !== '/projects') {
        navigate('/projects')
      }
    } catch (err) {
      toast.error(t('deleteFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog
      triggerElement={null}
      isOpen={isOpen}
      onClose={onClose}
      title={title || t('title')}
      content={content || <Typography>{t('description')}</Typography>}
      paperStyles={{ width }}
      actionButtons={
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            label={t('cancelButton')}
            onClick={onClose}
            textTransform="uppercase"
          />
          <Button
            variant="contained"
            label={t('deleteButton')}
            onClick={handleDelete}
            loading={isLoading}
            textTransform="uppercase"
          />
        </Box>
      }
    />
  )
}

export default DeleteProject
